import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '../../../../../styles/Calendar.css';
import Button from '../../../../designSystem/Button/Button';
import { ADD_CONSULTATIONS_DATES_QUERY_KEY, queryAddDates } from './CreateConsultThirdStep.query';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useMutation, useQueryClient } from 'react-query';
import {
  CreateConsultThirdStepFormSchema,
  CreateConsultThirdStepMutation,
} from './CreateConsultThirdStep.types';
import { AddConsultationDatesRequestData, ConsultationScope } from '../../../../../types/models/consultations/consultation.types';
import { useConsultationSummary } from '../../../../../hooks/fetchers/useConsultationSummary/useConsultationSummary';
import { useForm, Controller } from 'react-hook-form';
import { getCreateConsultThirdStepSchema } from './CreateConsultThirdStep.schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { createConsultThirdStepData } from './CreateConsultThirdStep.data';
import Input from '../../../../commons/Input/Input';
import { ButtonColor, ButtonSize } from '../../../../designSystem/Button/Button.types';
import { StepWizardContext } from '../../../../commons/StepWizard/StepWizard.context';
import Select from '../../../../commons/Select/Select';
import { useCurrentEntity } from '../../../../../hooks/fetchers/useCurrentEntity/useCurrentEntity';
import { SelectOption } from '../../../../commons/Select/Select.types';
moment.locale('fr');

export default () => {
  const { consultationId } = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { data: currentEntity } = useCurrentEntity()

  const { setCurrentStep } = useContext(StepWizardContext);

  if (!consultationId) {
    navigate('/dashboard/nouvelle_consultation');
    return <></>;
  }

  const { data } = useConsultationSummary(parseInt(consultationId));

  const [scope, setScope] = useState<ConsultationScope>(ConsultationScope.Department)

  const [dates, setDates] = useState<Date[]>(
    data?.infos.start && data?.infos.end ? [data?.infos.start as Date, data?.infos.end as Date] : [moment().toDate(), moment().add(3, 'days').toDate()]
  );

  const onDateChange = ([start, end]: Date[]) => {
    setDates([start, end]);
  };

  const [isVisitEnabled, setVisitEnabled] = useState(false);

  const formProps = useForm<CreateConsultThirdStepFormSchema>({
    resolver: isVisitEnabled ? yupResolver(getCreateConsultThirdStepSchema) : undefined,
    mode: 'onChange',
    reValidateMode: 'onChange',
    ...(data &&
      data.infos.firstName && {
        defaultValues: {
          firstName: data.infos.firstName,
          lastName: data.infos.lastName as string,
          email: data.infos.email as string,
          phone: data.infos.phone as string,
        },
      }),
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = formProps;

  useEffect(() => {
    if (data && data.infos.start && data.infos.end) {
      setDates([data.infos.start, data.infos.end]);
    }
  }, [data]);

  const { mutate } = useMutation<
    Awaited<ReturnType<CreateConsultThirdStepMutation>>,
    Error,
    AddConsultationDatesRequestData
  >((data) => queryAddDates(parseInt(consultationId), data), {
    mutationKey: ADD_CONSULTATIONS_DATES_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      setCurrentStep(4);
      navigate(`/dashboard/nouvelle_consultation/${consultationId}`);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const onSubmit = handleSubmit(
    (data) => mutate({ ...data, start: dates[0], end: dates[1], scope }),
    (errors) => console.error(errors)
  );

  const scopeOptions = useMemo(
    () => ([
      {
        label: `Département ${currentEntity ? `(${currentEntity.department})` : ''}`,
        value: ConsultationScope.Department
      },
      {
        label: `Région ${currentEntity ? `(${currentEntity.region})` : ''}`,
        value: ConsultationScope.Region
      },
      {
        label: 'Toute la france',
        value: ConsultationScope.All
      }
    ]),
    [currentEntity]
  )

  const currentFormData = createConsultThirdStepData.map((row) =>
    row.filter((item) => {
      if (getCreateConsultThirdStepSchema['_nodes'].includes(item.name)) {
        return item;
      }
    })
  );

  const isNextButtonEnabled = useMemo(() => {
    if (isVisitEnabled) {
      return isValid;
    } else {
      return dates.length === 2;
    }
  }, [isVisitEnabled, isValid, dates]);

  return (
    <>
      <Helmet>
        <title>Étape 4 - Nouvelle consultation</title>
      </Helmet>
      <form className="form">
        <Calendar
          // @ts-ignore
          value={dates as Date[]}
          // @ts-ignore
          onChange={onDateChange}
          selectRange
        />
        <Select 
          label='Zone géographique'
          options={scopeOptions}
          defaultValue={scopeOptions[0]}
          // @ts-ignore
          onChange={({ value }: SelectOption) => {
            setScope(value as ConsultationScope)
          }}
          style={{ marginBottom: 'var(--spacing-sToM)'}}
        />
        {currentFormData.map((row, rowIndex) => (
          <div key={rowIndex} className="form__row">
            {row.map(({ name, placeholder, label, type, ...props }, inputIndex) => (
              <Controller
                key={inputIndex}
                name={name}
                render={({ field: { onChange, value } }) => {
                  if (isVisitEnabled) {
                    return (
                      <Input
                        placeholder={placeholder || ''}
                        type={type}
                        error={errors[name]?.message as string}
                        // @ts-ignore
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          if (name === 'visitOnSite') {
                            const isChecked = Boolean(e.target.checked);
                            if (isChecked) {
                              setVisitEnabled(true);
                            } else {
                              setVisitEnabled(false);
                            }
                          }
                        }}
                        {...{ label }}
                        {...props}
                      />
                    );
                  } else {
                    if (name === 'visitOnSite') {
                      return (
                        <Input
                          placeholder={placeholder || ''}
                          type={type}
                          error={errors[name]?.message as string}
                          // @ts-ignore
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            const isChecked = Boolean(e.target.checked);
                            if (isChecked) {
                              setVisitEnabled(true);
                            } else {
                              setVisitEnabled(false);
                            }
                          }}
                          {...{ label }}
                          {...props}
                        />
                      );
                    } else {
                      return <></>;
                    }
                  }
                }}
                {...{ control }}
              />
            ))}
          </div>
        ))}
        <Button
          style={{ marginBottom: 'var(--spacing-s)' }}
          color={ButtonColor.Contrast}
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard/consultations');
          }}>
          Continuer plus tard
        </Button>
        <Button
          style={{ marginBottom: 'var(--spacing-s)' }}
          onClick={(e) => {
            e.preventDefault();
            setCurrentStep(2);
          }}>
          Précedent
        </Button>
        <Button
          style={{ alignSelf: 'unset' }}
          onClick={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          size={ButtonSize.Large}
          disabled={!isNextButtonEnabled}>
          Suivant
        </Button>
      </form>
    </>
  );
};
